import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import i18n from "../../i18n";

let fetchGamesAbortController;
let fetchMoreGamesAbortController;

const initialState = {
  games: [],
  currentPage: 1,
  totalPages: 1,
  currentCategoryId: null,

  featureTags: [],
  selectedFeatureTags: [],

  volatilityOptions: [],
  selectedVolatilityOptions: [],

  paylinesMin: 0,
  paylinesMax: 0,
  selectedPaylinesMin: 0,
  selectedPaylinesMax: 0,

  multiplierMin: 0,
  multiplierMax: 0,
  selectedMultiplierMin: 0,
  selectedMultiplierMax: 0,

  sortBy: 'releaseDate',
  sortDirection: 'desc',

  gamesLoading: false,
  catalogLoading: true,
};

const fetchGamesRequest = async ({
  categoryId = null,
  page = 1,
  featureTags = [],
  volatilityOptions = [],
  paylinesMin = null,
  paylinesMax = null,
  multiplierMin = null,
  multiplierMax = null,
  sortBy = null,
  sortDirection = null,
  locale = i18n.language,
  abortSignal
} = {}) => {

  const params = new URLSearchParams({
    offsetPage: page,
  });

  if (categoryId) params.append('tagCode', categoryId);
  if (featureTags) params.append('featureTags', featureTags);
  if (volatilityOptions) params.append('volatilityOptions', volatilityOptions);
  if (paylinesMin) params.append('paylinesMin', paylinesMin);
  if (paylinesMax) params.append('paylinesMax', paylinesMax);
  if (multiplierMin) params.append('multiplierMin', multiplierMin);
  if (multiplierMax) params.append('multiplierMax', multiplierMax);
  if (sortBy) params.append('sortBy', sortBy);
  if (sortDirection) params.append('sortDirection', sortDirection);
  params.append('locale', locale);

  const response = await fetch(`/fetchGames?${params.toString()}`, {
    signal: abortSignal
  });

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  return response.json();
}

export const fetchCatalogFiltersData = createAsyncThunk(
  'catalog/fetchCatalogFiltersData',
  async ({ }) => {
    const response = await fetch(`/games/fetchCatalogFiltersData`);

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const data = await response.json();
    return data;
  }
)

export const fetchGames = createAsyncThunk(
  'catalog/fetchGames',
  async ({ page }, { getState }) => {
    const state = getState().catalog;

    // Cancel the previous request if it exists
    if (fetchGamesAbortController) {
      fetchGamesAbortController.abort();
    }

    // Create a new abort controller for this request
    fetchGamesAbortController = new AbortController();
    const { signal: abortSignal } = fetchGamesAbortController;

    return await fetchGamesRequest({
      categoryId: state.currentCategoryId,
      page: page,
      featureTags: state.selectedFeatureTags,
      volatilityOptions: state.selectedVolatilityOptions,
      paylinesMin: state.selectedPaylinesMin !== state.paylinesMin ? state.selectedPaylinesMin : null,
      paylinesMax: state.selectedPaylinesMax !== state.paylinesMax ? state.selectedPaylinesMax : null,
      multiplierMin: state.selectedMultiplierMin !== state.multiplierMin ? state.selectedMultiplierMin : null,
      multiplierMax: state.selectedMultiplierMax !== state.multiplierMax ? state.selectedMultiplierMax : null,
      sortBy: state.sortBy !== initialState.sortBy ? state.sortBy : null,
      sortDirection: state.sortDirection !== initialState.sortDirection ? state.sortDirection : null,
      abortSignal
    });
  }
);

export const fetchMoreGames = createAsyncThunk(
  'catalog/fetchMoreGames',
  async ({ page }, { getState }) => {
    const state = getState().catalog;

    // Cancel the previous request if it exists
    if (fetchMoreGamesAbortController) {
      fetchMoreGamesAbortController.abort();
    }

    // Create a new abort controller for this request
    fetchMoreGamesAbortController = new AbortController();
    const { signal: abortSignal } = fetchMoreGamesAbortController;

    return await fetchGamesRequest({
      categoryId: state.currentCategoryId,
      page: page,
      featureTags: state.selectedFeatureTags,
      volatilityOptions: state.selectedVolatilityOptions,
      paylinesMin: state.selectedPaylinesMin !== state.paylinesMin ? state.selectedPaylinesMin : null,
      paylinesMax: state.selectedPaylinesMax !== state.paylinesMax ? state.selectedPaylinesMax : null,
      multiplierMin: state.selectedMultiplierMin !== state.multiplierMin ? state.selectedMultiplierMin : null,
      multiplierMax: state.selectedMultiplierMax !== state.multiplierMax ? state.selectedMultiplierMax : null,
      sortBy: state.sortBy !== initialState.sortBy ? state.sortBy : null,
      sortDirection: state.sortDirection !== initialState.sortDirection ? state.sortDirection : null,
      abortSignal
    });
  }
);

export const fetchCategoryGamesSpecial = createAsyncThunk(
  'catalog/fetchCategoryGamesSpecial',
  async ({ categoryId }) => {
    const response = await fetch(`/secret-catalog/games?tagCode=${categoryId ?? ''}`);

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const data = await response.json();
    return data;
  }
);

export const catalogSlice = createSlice({
  name: 'catalog',
  initialState: initialState,
  reducers: {
    changeCategory: (state, action) => {
      state.currentCategoryId = action.payload;
      state.currentPage = 1;
      state.totalPages = 1;
    },
    nextPage: (state) => {
      state.currentPage += 1;
    },
    crearFilters: (state) => {
      state.currentCategoryId = null;
      state.selectedFeatureTags = [];
      state.selectedVolatilityOptions = [];
      state.selectedPaylinesMin = state.paylinesMin;
      state.selectedPaylinesMax = state.paylinesMax;
      state.selectedMultiplierMin = state.multiplierMin;
      state.selectedMultiplierMax = state.multiplierMax;
      state.sortBy = initialState.sortBy;
      state.sortDirection = initialState.sortDirection;
    },
    addFeatureTag: (state, action) => {
      if (!state.selectedFeatureTags.includes(action.payload)) {
        state.selectedFeatureTags = [...state.selectedFeatureTags, action.payload];
      }
    },
    removeFeatureTag: (state, action) => {
      state.selectedFeatureTags = state.selectedFeatureTags.filter(
        (tag) => tag !== action.payload
      );
    },
    addVolatilityOption: (state, action) => {
      if (!state.selectedVolatilityOptions.includes(action.payload)) {
        state.selectedVolatilityOptions = [...state.selectedVolatilityOptions, action.payload];
      }
    },
    removeVolatilityOption: (state, action) => {
      state.selectedVolatilityOptions = state.selectedVolatilityOptions.filter(
        (code) => code !== action.payload
      );
    },
    setSelectedPaylines: (state, action) => {
      state.selectedPaylinesMin = action.payload.min;
      state.selectedPaylinesMax = action.payload.max;
    },
    setSelectedMultipliers: (state, action) => {
      state.selectedMultiplierMin = action.payload.min;
      state.selectedMultiplierMax = action.payload.max;
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    setSortDirection: (state, acttion) => {
      state.sortDirection = acttion.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCatalogFiltersData.pending, (state) => {
        state.catalogLoading = true;
      })
      .addCase(fetchCatalogFiltersData.fulfilled, (state, action) => {
        const paylinesMin = action.payload.paylines.min;
        const paylinesMax = action.payload.paylines.max;
        const multiplierMin = 0;
        const multiplierMax = 10000;

        state.featureTags = action.payload.feature_tags;
        state.volatilityOptions = action.payload.volatility_options;
        state.paylinesMin = paylinesMin;
        state.paylinesMax = paylinesMax;
        state.selectedPaylinesMin = paylinesMin;
        state.selectedPaylinesMax = paylinesMax;
        state.multiplierMin = multiplierMin;
        state.multiplierMax = multiplierMax;
        state.selectedMultiplierMin = multiplierMin;
        state.selectedMultiplierMax = multiplierMax;
        state.catalogLoading = false;
      })
      .addCase(fetchCatalogFiltersData.rejected, (state) => {
        state.catalogLoading = false;
      })
      .addCase(fetchGames.pending, (state) => {
        state.gamesLoading = true;
        state.games = [];
      })
      .addCase(fetchGames.fulfilled, (state, action) => {
        state.gamesLoading = false;
        state.games = action.payload.items;
        state.currentPage = action.payload.current_page;
        state.totalPages = action.payload.total_pages;
      })
      .addCase(fetchGames.rejected, (state) => {
        state.gamesLoading = false;
      })
      .addCase(fetchMoreGames.pending, (state) => {
        state.gamesLoading = true;
      })
      .addCase(fetchMoreGames.fulfilled, (state, action) => {
        state.gamesLoading = false;
        state.games = state.games.concat(action.payload.items);
        state.currentPage = action.payload.current_page;
        state.totalPages = action.payload.total_pages;
      })
      .addCase(fetchMoreGames.rejected, (state) => {
        state.gamesLoading = false;
      })
      .addCase(fetchCategoryGamesSpecial.pending, (state) => {
        state.gamesLoading = true;
        state.games = [];
      })
      .addCase(fetchCategoryGamesSpecial.fulfilled, (state, action) => {
        state.gamesLoading = false;
        state.games = action.payload;
      })
      .addCase(fetchCategoryGamesSpecial.rejected, (state, action) => {
        state.gamesLoading = false;
      })
  },
})

export const {
  changeCategory,
  nextPage,
  crearFilters,
  addFeatureTag,
  removeFeatureTag,
  addVolatilityOption,
  removeVolatilityOption,
  setSelectedPaylines,
  setSelectedMultipliers,
  setSortBy,
  setSortDirection
} = catalogSlice.actions;

export default catalogSlice.reducer
